import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

/**
 * Auth-callback pagina voor Emergent-managed Google OAuth.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Flow:
 *  1. Google → terug naar deze pagina met `#session_id=...` in URL hash
 *  2. We sturen session_id naar backend → krijgen JWT terug
 *  3. JWT in localStorage opslaan (zelfde key als bcrypt-login)
 *  4. Redirect naar /dashboard
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    (async () => {
      const hash = window.location.hash || '';
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) {
        setError('Geen session_id ontvangen — Google login mislukt');
        setTimeout(() => navigate('/login'), 2500);
        return;
      }
      const sessionId = decodeURIComponent(m[1]);
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.post(`${apiBase}/api/auth/google-session`, { session_id: sessionId }, { withCredentials: true });
        const d = res.data || {};
        if (d.access_token) {
          try {
            localStorage.setItem('emergent_billing_token', d.access_token);
            if (d.refresh_token) localStorage.setItem('emergent_billing_refresh', d.refresh_token);
          } catch { /* localStorage kan blocked zijn */ }
          window.location.replace('/dashboard');
        } else {
          setError('Geen geldig token ontvangen — probeer opnieuw');
          setTimeout(() => navigate('/login'), 2500);
        }
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || 'Onbekende fout';
        setError(`Google login mislukt: ${msg}`);
        setTimeout(() => navigate('/login'), 3000);
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="auth-callback-page">
      <div className="text-center space-y-4">
        {error ? (
          <>
            <div className="text-5xl">❌</div>
            <p className="text-sm text-red-600 max-w-sm" data-testid="auth-callback-error">{error}</p>
            <p className="text-xs text-gray-500">Je wordt teruggestuurd naar de login-pagina...</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-700 font-medium">Bezig met inloggen via Google...</p>
          </>
        )}
      </div>
    </div>
  );
}
